// @ts-strict

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';
import { BaseApiResponse, DefaultApiType, IEnlargeSectionData } from '../..';
import { AddIVRPromotionMethodResponse, IEditIVRResponse, IIVRCreateConfigurationResponse, IVRConfigurationListData } from './ivr-configurations.service.types';
import { CampaignsConfigurations } from '../ppi/ppi.service.types';
import { map } from 'rxjs/operators';
import { EarnModelSingleInfoData } from '@app/shared/components/faq-informations/faq-informations-item/faq-informations-item.component';
import { EarnModelMenuItem } from '@app/shared/components/faq-informations/faq-informations.component';

@Injectable({
  providedIn: 'root'
})
export class IvrConfigurationsService {
  public addPromotionMethod = new Subject<boolean>();
  public numberAddedOrEdited = new Subject<object>();
  public activeCampaignsWithConfigurations = new Subject<CampaignsConfigurations>();

  public myConfigSection: IEnlargeSectionData = {
    name: 'ivr_section_configs',
    icon: 'levels',
    section: 'config',
    negative: 'informations'
  };
  public informationsSection: IEnlargeSectionData = {
    name: 'informations',
    icon: 'news',
    section: 'informations',
    negative: 'config'
  };

  public ivrInformation: EarnModelSingleInfoData[] = [
    {
      informationTitle: 'ivr_infos_whatIs',
      informationIcon: 'list',
      buttonLink: 'https://mylead.global/pl/blog/jak-zarabiac-na-ivr',
      buttonText: 'read_more',
      description: [
        `<h5 style="margin-bottom: 40px; line-height: 30px">${this.translate.instant('view_translations.ivr_general_info')}</h5>`,
        `<h6 class="font-weight-bold">${this.translate.instant('view_translations.how_much_you_can_earn')}</h6>`,
        `<p class="description">${this.translate.instant('view_translations.ivr_available_rates')}</p>`,
        `<p class="description">${this.translate.instant('view_translations.ivr_first_rate')}</p>`,
        `<p class="description">${this.translate.instant('view_translations.ivr_second_rate')}</p>`,
        `<h6 class="font-weight-bold">${this.translate.instant('view_translations.how_does_it_work')}</h6>`,
        `<p class="description">${this.translate.instant('view_translations.ivr_explanation')}</p>`
      ],
      class: 'welcome',
      singleButton: false
    },
    {
      informationTitle: 'faq',
      informationIcon: 'faq',
      buttonLink: '',
      buttonText: '',
      description: [],
      class: 'faq',
      singleButton: false
    },
    {
      informationTitle: 'regulations',
      informationIcon: 'warning',
      buttonLink: '',
      buttonText: 'get_regulations',
      description: [`<p class="description">${this.translate.instant('view_translations.ivr_regulations_text')}</p>`],
      class: 'regulations',
      singleButton: true
    }
  ];

  public earnModelInfoTopMenu: EarnModelMenuItem[] = [
    {
      name: 'welcome',
      translation: 'ivr_infos_whatIs',
      icon: 'list',
      active: true
    },
    {
      name: 'faq',
      translation: 'faq',
      icon: 'faq',
      active: false
    },
    {
      name: 'regulations',
      translation: 'regulations',
      icon: 'warning',
      active: false
    }
  ];

  public isPromotionPending = false;

  constructor(private readonly http: HttpClient, private readonly translate: TranslateService) {}

  public prepareIvrInformation = (faqDescription: string[]): EarnModelSingleInfoData[] => {
    return this.ivrInformation.map(information =>
      information.informationTitle === 'faq' ? { ...information, description: [...faqDescription] } : information
    );
  };

  public addIvrNumberConfiguration(name: string, amount: string, type: string): Observable<DefaultApiType<IEditIVRResponse>> {
    let body: { name: string; amount?: string; type: string } = {
      type: type === 'sociable' ? 'ivr_18' : 'ivr_ezo',
      name,
      amount
    };

    if (type === 'esoteric') {
      const { amount, ...rest } = body;

      body = { ...rest };
    }

    return this.http.post<DefaultApiType<IEditIVRResponse>>(environment.apiUrl + `/ivr/configuration/store`, body);
  }

  public updateIvrNumberConfiguration(configID: number, configName: string): Observable<DefaultApiType<IEditIVRResponse>> {
    const ivrNumberConfiguration = {
      name: configName,
      id: configID
    };

    return this.http.put<DefaultApiType<IEditIVRResponse>>(environment.apiUrl + '/ivr/configuration/update', ivrNumberConfiguration);
  }

  public getFieldsForIVRNumberConfigEdit(configID: number): Observable<BaseApiResponse<IEditIVRResponse>> {
    return this.http.get<BaseApiResponse<IEditIVRResponse>>(environment.apiUrl + `/ivr/configuration/${configID}/edit`);
  }

  public getIVRNumberConfigurations(): Observable<BaseApiResponse<IVRConfigurationListData>> {
    return this.http.get<BaseApiResponse<IVRConfigurationListData>>(environment.apiUrl + `/ivr/configuration/list`);
  }

  public getFieldsToAssignNumber(): Observable<BaseApiResponse<IIVRCreateConfigurationResponse>> {
    return this.http.get<BaseApiResponse<IIVRCreateConfigurationResponse>>(environment.apiUrl + `/ivr/configuration/create`);
  }

  public addIVRPromotionMethod(fieldValues: { description: string; website: string }): Observable<AddIVRPromotionMethodResponse> {
    let ivrPromotion: { description: string; website?: string } = {
      description: fieldValues.description,
      website: fieldValues.website
    };

    if (!fieldValues.website) {
      const { website, ...rest } = ivrPromotion;

      ivrPromotion = { ...rest };
    }

    return this.http.post<AddIVRPromotionMethodResponse>(environment.apiUrl + `/ivr/promotion/store`, ivrPromotion);
  }

  public getRegulations(): Observable<Blob> {
    return this.http.get(environment.apiUrl + `/ivr/regulations`, { responseType: 'blob' });
  }

  public getIvrPromotionStatus = (): Observable<{ promotion_status: number | null }> => {
    return this.getIVRNumberConfigurations().pipe(
      map(({ data, status_code }) => {
        if (status_code === 200 && data?.promotion_status !== undefined) {
          const { promotion_status } = data;

          return { promotion_status };
        }

        return { promotion_status: null };
      })
    );
  };
}
